import axios, { AxiosProgressEvent } from 'axios';
import { holoxrApiClient } from '@/lib/apiClient';
import {
  IGetPrivateMediaParams, IPatchPrivateMedia, IPrivateMedia,
  IPrivateMediaSharedUser, IUsePostPatchPrivateMediaShare,
  IUsePostPrivateMediaShareResponse,
} from '@/services/privateMedia/types';

async function getPrivateMedia({
  limit,
  sortBy,
  order,
  search,
  page,
}: Required<IGetPrivateMediaParams>): Promise<IPrivateMedia[]> {
  const params = new URLSearchParams();
  params.set('page[offset]', String(page));
  params.set('page[limit]', String(limit));
  params.set('sort', sortBy);
  params.set('order', order);

  if (search) params.set('filter[search]', search);

  const response = await holoxrApiClient.get('/private-medias', {
    params,
  });
  return response.data;
}

async function getPrivateMediaById(
  mediaUuid: IPrivateMedia['uuid'],
): Promise<IPrivateMedia> {
  const response = await holoxrApiClient.get(`/private-medias/${mediaUuid}`);
  return response.data;
}

async function patchPrivateMediaByIdMutationFn(mediaUuid: IPrivateMedia['uuid'], payload: IPatchPrivateMedia) : Promise<void> {
  const response = await holoxrApiClient.patch(`/private-medias/${mediaUuid}`, payload);
  return response.data;
}

async function getPreSignedPrivateMediaUploadUrl(fileName: string): Promise<string> {
  const response = await holoxrApiClient.get(`/private-medias/upload?fileName=${encodeURIComponent(fileName)}`);
  return response.data;
}

async function getPreSignedPrivateMediaDownloadUrl(privateMediaUuid: string): Promise<string> {
  const response = await holoxrApiClient.get(`/private-medias/${privateMediaUuid}/download`);
  return response.data;
}

async function deletePrivateMediaRevokeMutationFn(
  privateMediaUuid: string,
  userUuid : string,
): Promise<void> {
  const response = await holoxrApiClient.delete(`/private-medias/${privateMediaUuid}/revoke/${userUuid}`);
  return response.data;
}

async function postPrivateMediaShareMutationFn(
  privateMediaUuid: string,
  payload: IUsePostPatchPrivateMediaShare,
): Promise<IUsePostPrivateMediaShareResponse> {
  const response = await holoxrApiClient.post(`/private-medias/${privateMediaUuid}/share`, payload);
  return response.data;
}
async function patchPrivateMediaAcessMutationFn(
  privateMediaUuid: string,
  payload: IUsePostPatchPrivateMediaShare,
): Promise<string> {
  const response = await holoxrApiClient.patch(`/private-medias/${privateMediaUuid}/access`, payload);
  return response.data;
}

async function deletePrivateMediaByIdMutationFn(privateMediaUuid: string): Promise<void> {
  const response = await holoxrApiClient.delete(`/private-medias/${privateMediaUuid}`);
  return response.data;
}

async function uploadMediaToPresignedUrl(
  file: File,
  presignedUrl: string,
  onProgress: (progress: number) => void,
  onCancel: (cancelFn: () => void) => void,
): Promise<void> {
  const controller = new AbortController();
  onCancel(() => controller.abort());

  await axios.put(presignedUrl, file, {
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    timeout: 300_000, // ms
    signal: controller.signal,
    onUploadProgress: (e: AxiosProgressEvent) => {
      if (e.lengthComputable && (e?.total ?? 1) > 0) {
        const pct = (e.loaded / (e?.total ?? 1)) * 100;
        onProgress(pct);
      }
    },
    // Prevent any serialization; sending the File as-is
    transformRequest: [(data) => data],
    // Ensure no credentials are sent to S3 by accident
    withCredentials: false,
  });
}

async function getPrivateMediaAccessListById(mediaUuid
: string): Promise<IPrivateMediaSharedUser[]> {
  const response = await holoxrApiClient.get(`/private-medias/${mediaUuid}/access`);
  return response.data;
}

export {
  getPrivateMedia,
  getPrivateMediaById,
  patchPrivateMediaByIdMutationFn,
  getPreSignedPrivateMediaUploadUrl,
  getPreSignedPrivateMediaDownloadUrl,
  deletePrivateMediaRevokeMutationFn,
  postPrivateMediaShareMutationFn,
  patchPrivateMediaAcessMutationFn,
  deletePrivateMediaByIdMutationFn,
  uploadMediaToPresignedUrl,
  getPrivateMediaAccessListById,
};
